import { useState } from "react"

import { getRateConPdfFn } from "@/api/tenderApi"
import { useRateConCountdown } from "@/management/hooks/useRateConCountdown"
import {
  defaultTimezone,
  IExecutionStrategySnapshotDto,
  IRateConStatusDto,
  StrategyType,
} from "@/shared/types"
import {
  downloadBlobFile,
  getRateConSnapshotsWithActivity,
  getSnapshotStrategyType,
  shouldShowCurrentRateConStatus,
  shouldShowRateConSection,
  showToast,
  strategyToText,
} from "@/shared/utils"

import { Button, Copy, Flex, Redacted, Stack, Title } from "@/shared/components"
import { IconDownloadDown } from "@/shared/icons"

import { RateConSnapshotRow } from "./RateConSnapshotRow"
import { RateConStatusBadge } from "./RateConStatusBadge"

interface IRateConSectionProps {
  rateConStatus?: IRateConStatusDto | null
  strategySnapshots?: IExecutionStrategySnapshotDto[]
  isLoading?: boolean
  timeZone?: string
}

export const RateConSection = ({
  rateConStatus,
  strategySnapshots = [],
  isLoading,
  timeZone = defaultTimezone,
}: IRateConSectionProps) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const showCurrentRateCon = shouldShowCurrentRateConStatus(rateConStatus)
  const countdown = useRateConCountdown(rateConStatus?.rateConConfirmationEndTime, timeZone)
  const snapshotTimeline = getRateConSnapshotsWithActivity(strategySnapshots)
  const showSnapshotTimeline = snapshotTimeline.length > 0

  if (isLoading) {
    return (
      <Stack space={16}>
        <Title as="h3" scale={6} color="theme-b-n3">
          Rate confirmation
        </Title>
        <Redacted height="22px" width="220px" text animated />
      </Stack>
    )
  }

  if (!shouldShowRateConSection(rateConStatus, strategySnapshots)) {
    return null
  }

  const handleDownload = async () => {
    if (!rateConStatus?.originalOrderId || !rateConStatus.orgId) {
      return
    }

    setIsDownloading(true)
    try {
      const blob = await getRateConPdfFn(rateConStatus.originalOrderId, rateConStatus.orgId)
      downloadBlobFile(blob, `rate-confirmation-${rateConStatus.originalOrderId}.pdf`)
    } catch {
      showToast({ type: "error", text: "Failed to download rate confirmation." })
    } finally {
      setIsDownloading(false)
    }
  }

  const isWaitingForRequest = rateConStatus?.status === "WAITING_FOR_RATE_CON_REQUEST"
  const isPendingConfirmation =
    rateConStatus?.status === "RATECON_PENDING_CONFIRMATION" &&
    rateConStatus.rateConConfirmationStatus === "PENDING"
  const isConfirmed = rateConStatus?.rateConConfirmationStatus === "CONFIRMED"
  const isDeclinedOrExpired =
    rateConStatus?.rateConConfirmationStatus === "DECLINED" ||
    rateConStatus?.rateConConfirmationStatus === "EXPIRED"

  return (
    <Stack space={16}>
      <Title as="h3" scale={6} color="theme-b-n3">
        Rate confirmation
      </Title>

      {showSnapshotTimeline ? (
        <Stack space={12}>
          <Copy scale={10} color="theme-n6-n5" fontWeight="bold">
            Strategy timeline
          </Copy>
          {snapshotTimeline.map((snapshot, index) => (
            <RateConSnapshotRow
              key={`${getSnapshotStrategyType(snapshot) || "strategy"}-${index}`}
              snapshot={snapshot}
              timeZone={timeZone}
            />
          ))}
        </Stack>
      ) : null}

      {showCurrentRateCon && rateConStatus && !showSnapshotTimeline ? (
        <Flex align="center" wrap css={{ gap: "$12" }}>
          <RateConStatusBadge
            status={rateConStatus.status}
            confirmationStatus={rateConStatus.rateConConfirmationStatus}
          />

          {isWaitingForRequest ? (
            <Copy scale={10} color="theme-b-n3">
              Rate confirmation pending — external system will request it
            </Copy>
          ) : null}

          {isPendingConfirmation && countdown ? (
            <Stack space={4}>
              <Copy scale={10} color="theme-b-n3" fontWeight="bold">
                {countdown.isExpired ? "Confirmation window expired" : countdown.countdownLabel}
              </Copy>
              <Copy scale={10} color="theme-n6-n5">
                {`Expires at ${countdown.expiresAtLabel}`}
              </Copy>
            </Stack>
          ) : null}

          {isConfirmed ? (
            <Copy scale={10} color="theme-b-n3">
              Carrier confirmed — order will complete
            </Copy>
          ) : null}

          {isDeclinedOrExpired ? (
            <Copy scale={10} color="special-error">
              {rateConStatus.rateConConfirmationStatus === "DECLINED"
                ? "Carrier declined — may move to next strategy"
                : "No response in time — may move to next strategy"}
            </Copy>
          ) : null}

          {rateConStatus.strategyType ? (
            <Copy scale={10} color="theme-n6-n5">
              {`Winning strategy: ${strategyToText(rateConStatus.strategyType as StrategyType)}`}
            </Copy>
          ) : null}

          {rateConStatus.externalRateConId ? (
            <Copy scale={10} color="theme-n6-n5">
              {`TMS reference: ${rateConStatus.externalRateConId}`}
            </Copy>
          ) : null}
        </Flex>
      ) : null}

      {rateConStatus?.externalRateConId && showSnapshotTimeline ? (
        <Copy scale={10} color="theme-n6-n5">
          {`TMS reference: ${rateConStatus.externalRateConId}`}
        </Copy>
      ) : null}

      <Flex align="center" wrap css={{ gap: "$12" }}>
        {rateConStatus?.pdfAvailable ? (
          <Button
            action="secondary"
            size="small"
            type="button"
            ariaLabel="Download rate confirmation"
            icon={<IconDownloadDown />}
            loading={isDownloading}
            onClick={handleDownload}
          >
            <Copy as="span" fontWeight="bold" css={{ whiteSpace: "nowrap" }}>
              Download rate confirmation
            </Copy>
          </Button>
        ) : null}

        {rateConStatus?.rateConSent ? (
          <Flex
            align="center"
            css={{
              height: "$24",
              borderRadius: "$32",
              padding: "$2 $8",
              backgroundColor: "$specials-info",
              width: "fit-content",
            }}
          >
            <Copy as="span" scale={10} color="system-white" fontWeight="bold">
              Email sent to carrier
            </Copy>
          </Flex>
        ) : null}
      </Flex>
    </Stack>
  )
}
